import React, { useState } from 'react';
import { Table, Tag, Tooltip, Progress } from 'antd';
import { QuestionCircleOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DriverTaskData, AreaTaskData } from '../types';
import { TaskMonitoringFilters, ViewMode } from './TaskMonitoringFilter';

interface TaskMonitoringTableProps {
    data: (DriverTaskData | AreaTaskData)[];
    onDetailClick: (type: string, record: DriverTaskData | AreaTaskData) => void;
    viewMode: ViewMode;
    tableView: 'dspPerspective' | 'whsPerspective' | 'driverDimension' | 'areaDimension';
    loading?: boolean;
    pagination?: {
        page: number;
        size: number;
        totalElements: number;
        totalPages: number;
        hasNext: boolean;
        hasPrevious: boolean;
    };
    sortConfig?: {
        sortBy: string;
        sortDirection: 'ASC' | 'DESC';
    };
    onSortChange?: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
    filters: TaskMonitoringFilters;
}

const TaskMonitoringTable: React.FC<TaskMonitoringTableProps> = ({
    data,
    onDetailClick,
    viewMode,
    tableView,
    loading = false,
    pagination,
    sortConfig: propSortConfig,
    onSortChange,
    filters
}) => {
    const { t } = useTranslation();
    
    // 判断是否为司机维度（Daily 页面：'driver'）
    const isDriverMode = viewMode === 'driver';
    
    // 排序状态管理 - 使用 props 或默认值（默认按应派件量从高到低）
    const sortConfig = propSortConfig || {
        sortBy: 'allocatedParcels',
        sortDirection: 'DESC' as 'ASC' | 'DESC'
    };

    // 处理排序点击
    const handleSortClick = (field: string) => {
        let newDirection: 'ASC' | 'DESC' = 'ASC';
        
        if (sortConfig.sortBy === field) {
            newDirection = sortConfig.sortDirection === 'DESC' ? 'ASC' : 'DESC';
        }
        
        // 调用父组件的排序回调
        if (onSortChange) {
            onSortChange(field, newDirection);
        }
    };

    // 渲染排序图标（图标与包裹 span 均设 cursor: pointer，保证 daily/hub 两页表头悬停均为手型）
    const sortIconStyle = { cursor: 'pointer' as const };
    const renderSortIcon = (field: string) => {
        if (sortConfig.sortBy !== field) {
            return <SortAscendingOutlined style={{ color: '#d9d9d9', ...sortIconStyle }} />;
        }
        return sortConfig.sortDirection === 'ASC'
            ? <SortDescendingOutlined style={{ color: '#1890ff', ...sortIconStyle }} />
            : <SortAscendingOutlined style={{ color: '#1890ff', ...sortIconStyle }} />;
    };

    // 计算妥投率进度条颜色
    // 规则：<30% 红色；30% <= DSR < 70% 黄色；70% <= DSR < 100% 蓝色；=100% 绿色
    const getDeliveryRateColor = (rate: number): string => {
        if (rate === 100) {
            return '#52c41a'; // 绿色
        } else if (rate >= 70) {
            return '#1890ff'; // 蓝色
        } else if (rate >= 30) {
            return '#faad14'; // 黄色
        } else {
            return '#ff4d4f'; // 红色
        }
    };

    // 计算失败率进度条颜色（反向规则：失败率越低越好）
    // 规则：<30% 绿色（好）；30% <= 失败率 < 70% 黄色（中等）；70% <= 失败率 < 100% 蓝色（差）；=100% 红色（最差）
    const getFailedDeliveryRateColor = (rate: number): string => {
        if (rate === 100) {
            return '#ff4d4f'; // 红色（最差）
        } else if (rate >= 70) {
            return '#1890ff'; // 蓝色（差）
        } else if (rate >= 30) {
            return '#faad14'; // 黄色（中等）
        } else {
            return '#52c41a'; // 绿色（好）
        }
    };

    const renderRateBar = (rate: number) => {
        const safeRate = Number.isFinite(rate) ? Math.max(0, Math.min(100, rate)) : 0;
        const rateText = safeRate.toFixed(2);
        return (
        <div style={{
            position: 'relative',
            width: '60px',
            height: '24px',
            background: '#f0f0f0',
            borderRadius: '12px',
            overflow: 'hidden',
            margin: 0
        }}>
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: `${safeRate}%`,
                background: getDeliveryRateColor(safeRate),
                borderRadius: '12px',
                transition: 'width 0.3s ease'
            }} />
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '11px',
                fontWeight: '600',
                color: '#333',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                zIndex: 1
            }}>
                {rateText}%
            </div>
        </div>
        );
    };

    // 司机维度列定义
    const getDriverColumns = () => {
        const cols = [
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.serialNumber'),
                key: 'serialNumber',
                width: 50,
                align: 'left' as const,
                fixed: 'left' as const,
                render: (_: any, __: any, index: number) => {
                    const currentPage = pagination?.page || 1;
                    const pageSize = pagination?.size || 5;
                    return (currentPage - 1) * pageSize + index + 1;
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.driver'),
                dataIndex: 'driverName',
                key: 'driverName',
                width: 140,
                align: 'left' as const,
                fixed: 'left' as const,
                ellipsis: false,
                render: (text: string) => <span>{text || '-'}</span>
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.dsp'),
                dataIndex: 'dsp',
                key: 'dsp',
                width: 120,
                align: 'left' as const,
                ellipsis: false,
                render: (text: string) => <span>{text || '-'}</span>
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.deliveredActualScheduled'),
                key: 'deliveredActualPickedUp',
                align: 'left' as const,
                ellipsis: true,
                render: (_: any, record: DriverTaskData) => {
                    // 分子：已妥投数量（使用 deliveredCount）
                    const totalDelivered = record.deliveredCount ?? 0;
                    // 分母：司机实取 + 当日转入
                    const actualPickedUp = record.pickedUpFromWarehouse + record.transferredIn;
                    
                    const clickable = totalDelivered > 0 || actualPickedUp > 0;
                    return clickable ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('delivered', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {`${totalDelivered}/${actualPickedUp}`}
                        </a>
                    ) : (
                        <span>{`${totalDelivered}/${actualPickedUp}`}</span>
                    );
                }
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.areaColumns.deliverySuccessRate')}
                        <span onClick={() => handleSortClick('deliveryRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('deliveryRate')}
                        </span>
                    </span>
                ),
                dataIndex: 'deliveryRate',
                key: 'deliveryRate',
                width: 120,
                align: 'left' as const,
                render: (rate: number) => (
                <div style={{ 
                    position: 'relative', 
                    width: '70px', 
                    height: '24px',
                    background: '#f0f0f0',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    margin: '0 auto'
                }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${rate}%`,
                            background: getDeliveryRateColor(rate),
                            borderRadius: '12px',
                            transition: 'width 0.3s ease'
                        }} />
                        <div style={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)', 
                            fontSize: '11px', 
                            fontWeight: '600',
                            color: '#333',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                            zIndex: 1
                        }}>
                            {rate}%
                        </div>
                    </div>
                )
            },
            {
                title: (
                    <div>
                        <div>{t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels')}</div>
                        <div>{t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcelsAllocatedTransferredInHint')}</div>
                    </div>
                ),
                dataIndex: 'allocatedParcels',
                key: 'allocatedParcels',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    const allocated = record.allocatedParcels ?? 0;
                    const transferred = record.transferredIn ?? 0;
                    const scheduled = allocated + transferred;
                    if (scheduled === 0) {
                        return '0+0';
                    }
                    return (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {allocated > 0 ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('allocatedParcels', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {allocated}
                                </a>
                            ) : (
                                <span>{allocated}</span>
                            )}
                            <span style={{ margin: '0 2px' }}>+</span>
                            {transferred > 0 ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('transferred_in', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {transferred}
                                </a>
                            ) : (
                                <span>{transferred}</span>
                            )}
                        </span>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.actualScheduledToday'),
                children: [
                    {
                        title: t('wms.delivery.dailyTaskMonitoring.areaColumns.driverWarehousePickup'),
                        dataIndex: 'pickedUpFromWarehouse',
                        key: 'pickedUpFromWarehouse',
                        width: 120,
                        align: 'left' as const,
                        render: (text: number, record: DriverTaskData) => {
                            if (text === 0) {
                                return text;
                            }
                            return (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('pickedUpFromWarehouse', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {text}
                                </a>
                            );
                        }
                    },
                    {
                        title: t('wms.delivery.dailyTaskMonitoring.areaColumns.transferInToday'),
                        dataIndex: 'transferredIn',
                        key: 'transferredIn',
                        width: 80,
                        align: 'left' as const,
                        render: (text: number, record: DriverTaskData) => {
                            if (text === 0) {
                                return text;
                            }
                            return (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('transferred_in', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {text}
                                </a>
                            );
                        }
                    },
                    {
                        title: t('wms.delivery.dailyTaskMonitoring.areaColumns.transferOutToday'),
                        dataIndex: 'transferredOut',
                        key: 'transferredOut',
                        width: 80,
                        align: 'left' as const,
                        render: (text: number, record: DriverTaskData) => {
                            if (text === 0) {
                                return text;
                            }
                            return (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('transferred_out', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {text}
                                </a>
                            );
                        }
                    }
                ]
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.delivering'),
                dataIndex: 'inDelivery',
                key: 'inDelivery',
                width: 80,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    if (text === 0) {
                        return text;
                    }
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('inDelivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.onTimeDelivery'),
                dataIndex: 'onTimeDelivery',
                key: 'onTimeDelivery',
                width: 80,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    if (text === 0) {
                        return text;
                    }
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('on_time_delivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.delayedDelivery'),
                dataIndex: 'overdueDelivery',
                key: 'overdueDelivery',
                width: 80,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    if (text === 0) {
                        return text;
                    }
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('overdue_delivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.noAscanDelivery'),
                dataIndex: 'noAscanDelivery',
                key: 'noAscanDelivery',
                width: 150,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    const noAscanDelivery = (record as any).noAscanDelivery ?? 0;
                    return noAscanDelivery > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('no_ascan_delivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {noAscanDelivery}
                        </a>
                    ) : (
                        <span>{noAscanDelivery}</span>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.missingInWarehouse'),
                dataIndex: 'warehouseLost',
                key: 'warehouseLost',
                width: 150,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    if (text === 0) {
                        return text;
                    }
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('warehouseLost', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.unclaimed'),
                dataIndex: 'unclaimedCount',
                key: 'unclaimedCount',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    if (text === 0 || !text) {
                        return text ?? 0;
                    }
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('unclaimed', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.failedNotReturned'),
                dataIndex: 'damaged',
                key: 'damaged',
                width: 150,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    if (text === 0) {
                        return text;
                    }
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('damaged', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.failedReturned'),
                dataIndex: 'returned',
                key: 'returned',
                width: 150,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    if (text === 0) {
                        return text;
                    }
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('returned', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.lastDeliveryTime'),
                dataIndex: 'lastDeliveryTime',
                key: 'lastDeliveryTime',
                width: 180,
                align: 'left' as const,
                render: (text: string) => {
                    if (!text) return '-';
                    try {
                        const date = dayjs(text);
                        if (!date.isValid()) return text;
                        return date.format('YYYY-MM-DD HH:mm:ss');
                    } catch (error) {
                        return text;
                    }
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.dwellTime'),
                dataIndex: 'dwellTime',
                key: 'dwellTime',
                width: 100,
                align: 'left' as const,
                render: (text: string, record: DriverTaskData) => {
                    // 优先使用后端返回的 dwellTime（单位：分钟，字符串）
                    // 后端在 inDelivery>0 时为“当前已停留时长”，inDelivery<=0 且有两个时间为“上一派件周期时长”
                    const parsedMinutesFromBackend = (() => {
                        if (!text) return null;
                        const n = Number(text);
                        return Number.isFinite(n) ? n : null;
                    })();

                    const diffMinutes = (() => {
                        if (parsedMinutesFromBackend !== null) return parsedMinutesFromBackend;
                        // 兼容：后端尚未返回时，仅在派送中包裹>0 用旧逻辑（当前时间-上一次妥投时间）
                        if (!record.inDelivery || record.inDelivery === 0) return null;
                        if (!record.lastDeliveryTime) return null;
                        const lastDeliveryTime = dayjs(record.lastDeliveryTime);
                        if (!lastDeliveryTime.isValid()) return null;
                        return dayjs().diff(lastDeliveryTime, 'minute');
                    })();

                    if (diffMinutes === null) return '-';

                    const hours = Math.floor(diffMinutes / 60);
                    const minutes = diffMinutes % 60;
                    const displayText = hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;

                    if (diffMinutes >= 30) {
                                    return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{displayText}</span>;
                    }
                                    return displayText;
                }
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.areaColumns.routeDimension')}
                        <span onClick={() => handleSortClick('failedDeliveryRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('failedDeliveryRate')}
                        </span>
                    </span>
                ),
                dataIndex: 'failedDeliveryRate',
                key: 'failedDeliveryRate',
                width: 150,
                align: 'left' as const,
                render: (rate: number) => (
                <div style={{ 
                    position: 'relative', 
                            width: '70px', 
                    height: '24px',
                    background: '#f0f0f0',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    margin: '0 auto'
                }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${rate}%`,
                            background: getFailedDeliveryRateColor(rate),
                            borderRadius: '12px',
                            transition: 'width 0.3s ease'
                        }} />
                        <div style={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)', 
                            fontSize: '11px', 
                            fontWeight: '600',
                            color: '#333',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                            zIndex: 1
                        }}>
                            {rate}%
                        </div>
                    </div>
                )
            }
        ];

        const serialCol = cols[0];
        const driverCol = cols[1];
        const dspCol = cols[2];
        const restCols = [
            {
                title: (
                    <div>
                        <div>
                            {t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels')}
                            <span onClick={() => handleSortClick('allocatedParcels')} style={{ cursor: 'pointer' }}>
                                {renderSortIcon('allocatedParcels')}
                            </span>
                        </div>
                        <div>{t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcelsAllocatedTransferredInHint')}</div>
                    </div>
                ),
                dataIndex: 'allocatedParcels',
                key: 'allocatedParcels',
                width: 110,
                align: 'left' as const,
                render: (text: number, record: DriverTaskData) => {
                    const allocated = record.allocatedParcels ?? 0;
                    const transferred = record.transferredIn ?? 0;
                    const scheduled = allocated + transferred;
                    if (scheduled === 0) {
                        return '0+0';
                    }
                    return (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {allocated > 0 ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('allocatedParcels', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {allocated}
                                </a>
                            ) : (
                                <span>{allocated}</span>
                            )}
                            <span style={{ margin: '0 2px' }}>+</span>
                            {transferred > 0 ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('transferred_in', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {transferred}
                                </a>
                            ) : (
                                <span>{transferred}</span>
                            )}
                        </span>
                    );
                }
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.tableColumns.pickupRate')}
                        <span onClick={() => handleSortClick('pickupRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('pickupRate')}
                        </span>
                    </span>
                ),
                key: 'pickupRate',
                width: 100,
                align: 'left' as const,
                render: (_: any, record: DriverTaskData) => {
                    const scheduled = (record.allocatedParcels ?? 0) + (record.transferredIn ?? 0);
                    const actualPickedUp = (record.pickedUpFromWarehouse ?? 0) + (record.transferredIn ?? 0);
                    const rate = scheduled > 0 ? (actualPickedUp / scheduled) * 100 : 0;
                    return renderRateBar(rate);
                }
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.tableColumns.deliveryRate')}
                        <span onClick={() => handleSortClick('deliveryRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('deliveryRate')}
                        </span>
                    </span>
                ),
                key: 'deliveryRate',
                width: 100,
                align: 'left' as const,
                render: (_: any, record: DriverTaskData) => {
                    const delivered = record.deliveredCount ?? 0;
                    const scheduledParcels = (record.allocatedParcels ?? 0) + (record.transferredIn ?? 0);
                    const rate = scheduledParcels > 0 ? (delivered / scheduledParcels) * 100 : 0;
                    return renderRateBar(rate);
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.deliveredOverActualPickedUp'),
                key: 'deliveredActualPickedUp',
                width: 140,
                align: 'left' as const,
                render: (_: any, record: DriverTaskData) => {
                    const delivered = record.deliveredCount ?? 0;
                    const actualPickedUp = (record.pickedUpFromWarehouse ?? 0) + (record.transferredIn ?? 0);
                    const clickableDelivered = delivered > 0;
                    const clickableActualPickedUp = actualPickedUp > 0;
                    return (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {clickableDelivered ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('delivered', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {delivered}
                                </a>
                            ) : (
                                <span>{delivered}</span>
                            )}
                            <span style={{ margin: '0 6px', color: '#999' }}>/</span>
                            {clickableActualPickedUp ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('pickedUpFromWarehouse', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {actualPickedUp}
                                </a>
                            ) : (
                                <span>{actualPickedUp}</span>
                            )}
                        </span>
                    );
                }
            },
            // 停留时长（口径由后端返回 dwellTime=分钟；前端做格式化 & 红色提示）
            // 后端在 inDelivery>0 时为“当前已停留时长”，inDelivery<=0 且有两个时间为“上一派件周期时长”
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.dwellTime'),
                dataIndex: 'dwellTime',
                key: 'dwellTime',
                width: 110,
                align: 'left' as const,
                render: (text: string, record: DriverTaskData) => {
                    const n = text != null && text !== '' ? Number(text) : NaN;
                    const diffMinutes = Number.isFinite(n) ? n : null;
                    if (diffMinutes === null) return '-';

                    const hours = Math.floor(diffMinutes / 60);
                    const minutes = diffMinutes % 60;
                    const displayText = hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;

                    if (diffMinutes >= 30) {
                        return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{displayText}</span>;
                    }
                    return displayText;
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked'),
                dataIndex: 'unclaimedCount',
                key: 'unclaimedCount',
                width: 90,
                align: 'left' as const,
                render: (value: number, record: DriverTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('unclaimed', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery'),
                dataIndex: 'inDelivery',
                key: 'inDelivery',
                width: 90,
                align: 'left' as const,
                render: (value: number, record: DriverTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('inDelivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned'),
                dataIndex: 'returned',
                key: 'returned',
                width: 120,
                align: 'left' as const,
                render: (value: number, record: DriverTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('returned', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
                dataIndex: 'damaged',
                key: 'damaged',
                width: 120,
                align: 'left' as const,
                render: (value: number, record: DriverTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('damaged', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost'),
                dataIndex: 'warehouseLost',
                key: 'warehouseLost',
                width: 120,
                align: 'left' as const,
                render: (value: number, record: DriverTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('warehouseLost', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            }
        ];

        const restColsForDspPerspective = tableView === 'dspPerspective'
            ? restCols.filter((col) => col.key !== 'dwellTime')
            : restCols;

        // 3、司机维度：首列DSP，第二列司机（与DSP视角保持一致的固定设置）
        if (tableView === 'driverDimension') {
            return [
                serialCol,
                { ...dspCol, fixed: 'left' as const },
                driverCol,
                ...restCols
            ];
        }

        // 1、DSP视角：首列DSP（固定首列），去掉司机列
        if (tableView === 'dspPerspective') {
            return [
                serialCol,
                { ...dspCol, fixed: 'left' as const },
                ...restColsForDspPerspective
            ];
        }

        return [serialCol, driverCol, dspCol, ...restCols];
    };

    // 区域维度列定义
    const getAreaColumns = () => {
        const cols = [
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.serialNumber'),
                key: 'serialNumber',
                width: 50,
                align: 'left' as const,
                fixed: 'left' as const,
                render: (_: any, __: any, index: number) => {
                    const currentPage = pagination?.page || 1;
                    const pageSize = pagination?.size || 5;
                    return (currentPage - 1) * pageSize + index + 1;
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.area'),
                dataIndex: 'area',
                key: 'area',
                width: 70,
                align: 'left' as const,
                fixed: 'left' as const,
                ellipsis: false,
                render: (text: string) => <span>{text || '-'}</span>
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.dsp'),
                dataIndex: 'dsp',
                key: 'dsp',
                width: 120,
                align: 'left' as const,
                ellipsis: false,
                render: (text: string) => <span>{text || '-'}</span>
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.deliveredActualPickup'),
                key: 'deliveredActualPickedUp',
                width: 140,
                align: 'left' as const,
                render: (_: any, record: AreaTaskData) => {
                    const delivered = record.deliveredCount ?? 0;
                    const actualPickedUp = (record.pickedUpFromWarehouse ?? 0) + (record.transferredIn ?? 0);
                    const clickableDelivered = delivered > 0;
                    const clickableActualPickedUp = actualPickedUp > 0;
                    return (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {clickableDelivered ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('delivered', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {delivered}
                                </a>
                            ) : (
                                <span>{delivered}</span>
                            )}
                            <span style={{ margin: '0 6px', color: '#999' }}>/</span>
                            {clickableActualPickedUp ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('pickedUpFromWarehouse', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {actualPickedUp}
                                </a>
                            ) : (
                                <span>{actualPickedUp}</span>
                            )}
                        </span>
                    );
                }
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.areaColumns.deliverySuccessRate')}
                        <span onClick={() => handleSortClick('deliveryRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('deliveryRate')}
                        </span>
                    </span>
                ),
                dataIndex: 'deliveryRate',
                key: 'deliveryRate',
                width: 100,
                align: 'left' as const,
                render: (rate: number) => (
                <div style={{ 
                    position: 'relative', 
                    width: '70px', 
                    height: '24px',
                    background: '#f0f0f0',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    margin: '0 auto'
                }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${rate}%`,
                            background: getDeliveryRateColor(rate),
                            borderRadius: '12px',
                            transition: 'width 0.3s ease'
                        }} />
                        <div style={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)', 
                            fontSize: '11px', 
                            fontWeight: '600',
                            color: '#333',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                            zIndex: 1
                        }}>
                            {rate}%
                        </div>
                    </div>
                )
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels')}
                        <span onClick={() => handleSortClick('allocatedParcels')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('allocatedParcels')}
                        </span>
                    </span>
                ),
                dataIndex: 'allocatedParcels',
                key: 'allocatedParcels',
                width: 150,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onDetailClick('allocatedParcels', record);
                        }}
                        style={{ color: '#1890ff' }}
                    >
                        {text}
                    </a>
                )
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.tableColumns.pickupRate')}
                        <span onClick={() => handleSortClick('pickupRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('pickupRate')}
                        </span>
                    </span>
                ),
                key: 'pickupRate',
                width: 100,
                align: 'left' as const,
                render: (_: any, record: AreaTaskData) => {
                    const scheduled = (record.allocatedParcels ?? 0) + (record.transferredIn ?? 0);
                    const actualPickedUp = (record.pickedUpFromWarehouse ?? 0) + (record.transferredIn ?? 0);
                    const rate = scheduled > 0 ? (actualPickedUp / scheduled) * 100 : 0;
                    return renderRateBar(rate);
                }
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.areaColumns.driverActualPickup')}
                        <Tooltip title={t('wms.delivery.dailyTaskMonitoring.explanations.driverActualPickup')}>
                            <QuestionCircleOutlined className="ml-1" />
                        </Tooltip>
                    </span>
                ),
                dataIndex: 'pickedUpFromWarehouse',
                key: 'pickedUpFromWarehouse',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onDetailClick('pickedUpFromWarehouse', record);
                        }}
                        style={{ color: '#1890ff' }}
                    >
                        {text}
                    </a>
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.delivering'),
                dataIndex: 'inDelivery',
                key: 'inDelivery',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => (
                    text > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('inDelivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    ) : (
                        <span>{text}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.onTimeDelivery'),
                dataIndex: 'onTimeDelivery',
                key: 'onTimeDelivery',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => (
                    text > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('on_time_delivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    ) : (
                        <span>{text}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.delayedDelivery'),
                dataIndex: 'overdueDelivery',
                key: 'overdueDelivery',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => (
                    text > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('overdue_delivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    ) : (
                        <span>{text}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.noAscanDelivery'),
                dataIndex: 'noAscanDelivery',
                key: 'noAscanDelivery',
                width: 150,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => {
                    const noAscanDelivery = (record as any).noAscanDelivery ?? 0;
                    return noAscanDelivery > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('no_ascan_delivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {noAscanDelivery}
                        </a>
                    ) : (
                        <span>{noAscanDelivery}</span>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.missingInWarehouse'),
                dataIndex: 'warehouseLost',
                key: 'warehouseLost',
                width: 150,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => (
                    text > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('warehouseLost', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    ) : (
                        <span>{text}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.unclaimed'),
                dataIndex: 'unclaimedCount',
                key: 'unclaimedCount',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => {
                    if (text === 0 || !text) {
                        return text ?? 0;
                    }
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('unclaimed', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.failedNotReturned'),
                dataIndex: 'damaged',
                key: 'damaged',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => (
                    text > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('damaged', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    ) : (
                        <span>{text}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.areaColumns.failedReturned'),
                dataIndex: 'returned',
                key: 'returned',
                width: 120,
                align: 'left' as const,
                render: (text: number, record: AreaTaskData) => (
                    text > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('returned', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {text}
                        </a>
                    ) : (
                        <span>{text}</span>
                    )
                )
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.areaColumns.routeDimension')}
                        <span onClick={() => handleSortClick('failedDeliveryRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('failedDeliveryRate')}
                        </span>
                    </span>
                ),
                dataIndex: 'failedDeliveryRate',
                key: 'failedDeliveryRate',
                width: 150,
                align: 'left' as const,
                render: (rate: number) => (
                <div style={{ 
                    position: 'relative', 
                    width: '70px', 
                    height: '24px',
                    background: '#f0f0f0',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    margin: '0 auto'
                }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            height: '100%',
                            width: `${rate}%`,
                            background: getFailedDeliveryRateColor(rate),
                            borderRadius: '12px',
                            transition: 'width 0.3s ease'
                        }} />
                        <div style={{ 
                            position: 'absolute', 
                            top: '50%', 
                            left: '50%', 
                            transform: 'translate(-50%, -50%)', 
                            fontSize: '11px', 
                            fontWeight: '600',
                            color: '#333',
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                            zIndex: 1
                        }}>
                            {rate}%
                        </div>
                    </div>
                )
            }
        ];

        const serialCol = cols[0];
        const areaCol = cols[1];
        const dspCol = cols[2];
        const restCols = [
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.tableColumns.scheduledParcels')}
                        <span onClick={() => handleSortClick('allocatedParcels')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('allocatedParcels')}
                        </span>
                    </span>
                ),
                dataIndex: 'allocatedParcels',
                key: 'allocatedParcels',
                width: 110,
                align: 'left' as const,
                render: (value: number, record: AreaTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('allocatedParcels', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.tableColumns.pickupRate')}
                        <span onClick={() => handleSortClick('pickupRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('pickupRate')}
                        </span>
                    </span>
                ),
                key: 'pickupRate',
                width: 100,
                align: 'left' as const,
                render: (_: any, record: AreaTaskData) => {
                    const scheduled = record.allocatedParcels ?? 0;
                    const pickedUp = record.pickedUpFromWarehouse ?? 0;
                    const rate = scheduled > 0 ? (pickedUp / scheduled) * 100 : 0;
                    return renderRateBar(rate);
                }
            },
            {
                title: (
                    <span>
                        {t('wms.delivery.dailyTaskMonitoring.tableColumns.deliveryRate')}
                        <span onClick={() => handleSortClick('deliveryRate')} style={{ cursor: 'pointer' }}>
                            {renderSortIcon('deliveryRate')}
                        </span>
                    </span>
                ),
                key: 'deliveryRate',
                width: 120,
                align: 'left' as const,
                render: (_: any, record: AreaTaskData) => {
                    const delivered = record.deliveredCount ?? 0;
                    const scheduledParcels = (record.allocatedParcels ?? 0) + (record.transferredIn ?? 0);
                    const rate = scheduledParcels > 0 ? (delivered / scheduledParcels) * 100 : 0;
                    return renderRateBar(rate);
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.deliveredOverActualPickedUp'),
                key: 'deliveredActualPickedUp',
                width: 140,
                align: 'left' as const,
                render: (_: any, record: AreaTaskData) => {
                    const delivered = record.deliveredCount ?? 0;
                    const actualPickedUp = (record.pickedUpFromWarehouse ?? 0) + (record.transferredIn ?? 0);
                    const clickableDelivered = delivered > 0;
                    const clickableActualPickedUp = actualPickedUp > 0;
                    return (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {clickableDelivered ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('delivered', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {delivered}
                                </a>
                            ) : (
                                <span>{delivered}</span>
                            )}
                            <span style={{ margin: '0 6px', color: '#999' }}>/</span>
                            {clickableActualPickedUp ? (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onDetailClick('pickedUpFromWarehouse', record);
                                    }}
                                    style={{ color: '#1890ff' }}
                                >
                                    {actualPickedUp}
                                </a>
                            ) : (
                                <span>{actualPickedUp}</span>
                            )}
                        </span>
                    );
                }
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.unpicked'),
                dataIndex: 'unclaimedCount',
                key: 'unclaimedCount',
                width: 90,
                align: 'left' as const,
                render: (value: number, record: AreaTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('unclaimed', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.inDelivery'),
                dataIndex: 'inDelivery',
                key: 'inDelivery',
                width: 90,
                align: 'left' as const,
                render: (value: number, record: AreaTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('inDelivery', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedReturned'),
                dataIndex: 'returned',
                key: 'returned',
                width: 120,
                align: 'left' as const,
                render: (value: number, record: AreaTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('returned', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.failedNotReturned'),
                dataIndex: 'damaged',
                key: 'damaged',
                width: 120,
                align: 'left' as const,
                render: (value: number, record: AreaTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('damaged', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            },
            {
                title: t('wms.delivery.dailyTaskMonitoring.tableColumns.warehouseLost'),
                dataIndex: 'warehouseLost',
                key: 'warehouseLost',
                width: 120,
                align: 'left' as const,
                render: (value: number, record: AreaTaskData) => (
                    value > 0 ? (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onDetailClick('warehouseLost', record);
                            }}
                            style={{ color: '#1890ff' }}
                        >
                            {value}
                        </a>
                    ) : (
                        <span>{value ?? 0}</span>
                    )
                )
            }
        ];

        // 1、DSP视角：首列DSP（固定首列），去掉区域列
        if (tableView === 'dspPerspective') {
            return [
                serialCol,
                { ...dspCol, fixed: 'left' as const },
                ...restCols
            ];
        }

        // 2、WHS视角：首列WHS（固定首列），去掉DSP和区域列
        if (tableView === 'whsPerspective') {
            const whsCol = {
                title: t('wms.delivery.dailyTaskMonitoring.buttons.whs'),
                dataIndex: 'warehouseName',
                key: 'warehouseName',
                width: 140,
                align: 'left' as const,
                fixed: 'left' as const,
                ellipsis: false,
                render: (text: string) => <span>{text || '-'}</span>
            };
            return [
                serialCol,
                { ...whsCol, fixed: 'left' as const },
                ...restCols
            ];
        }

        // 4、区域维度：首列DSP，第二列地区（与DSP视角保持一致的固定设置）
        // area 维度不需要停留时长列
        const restColsWithoutDwellTime = restCols.filter((col) => col.key !== 'dwellTime');
        if (tableView === 'areaDimension') {
            return [
                serialCol,
                { ...dspCol, fixed: 'left' as const },
                areaCol,
                ...restColsWithoutDwellTime
            ];
        }

        return [serialCol, areaCol, dspCol, ...restColsWithoutDwellTime];
    };

    const normalizeColumns = (cols: any[]): any[] => {
        return cols.map((col) => {
            const { fixed, width, ellipsis, children, title, ...rest } = col;
            const next: any = { ...rest };
            if (children) {
                next.children = normalizeColumns(children);
            }

            // 为表头添加换行支持，避免外语时表头被覆盖
            if (title) {
                next.title = () => (
                    <span style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: '1.2',
                        display: 'inline-block',
                        maxWidth: '100%'
                    }}>
                        {title}
                    </span>
                );
            }

            // 保留 fixed / width，尽量不换行，通过横向滚动展示
            if (typeof fixed !== 'undefined') next.fixed = fixed;
            if (col.key === 'serialNumber') {
                next.width = width ?? 48;
            } else if (typeof width !== 'undefined') {
                next.width = width;
            }

            // 尽量不换行：不折行，允许表格横向滚动展示完整内容
            next.ellipsis = typeof ellipsis !== 'undefined' ? ellipsis : false;
            next.onCell = () => ({
                style: {
                    whiteSpace: 'nowrap'
                }
            });

            return next;
        });
    };

    const columns = normalizeColumns(isDriverMode ? getDriverColumns() : getAreaColumns());

    return (
        <Table
            dataSource={data as any[]}
            columns={columns as any}
            rowKey="id"
            loading={loading}
            pagination={false}
            sticky
            size="small"
            tableLayout="auto"
            className="task-monitoring-table mb-4"
            scroll={{ x: 'max-content' }}
            components={{
                header: {
                    cell: (props: any) => (
                        <th
                            {...props}
                            style={{
                                ...props.style,
                                backgroundColor: '#fafafa',
                                fontWeight: 600,
                                fontSize: '14px',
                                color: '#262626',
                                borderBottom: '2px solid #f0f0f0',
                                textAlign: 'left',
                                whiteSpace: 'nowrap'
                            }}
                        />
                    )
                }
            }}
        />
    );
};

export default TaskMonitoringTable;